import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DRAFT_KEY = "tresses_booking_draft";

const readDraftState = () => {
    try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return { count: 0, owner: null };
        const d = JSON.parse(raw);
        return {
            count: Array.isArray(d?.selectedIds) ? d.selectedIds.length : 0,
            owner: d?.owner_email || null,
        };
    } catch { return { count: 0, owner: null }; }
};

/**
 * Tiny gold pill that surfaces in-progress booking drafts.
 * Hidden when: no draft, draft is empty, user is on /book or /admin,
 * OR the draft belongs to someone other than the currently signed-in user
 * (prevents leaking selections across accounts on a shared device).
 *
 * Listens for cross-tab "storage" events plus a custom "tresses-draft-changed"
 * event the booking page can dispatch on save/clear.
 */
export default function ResumeBookingPill() {
    const location = useLocation();
    const { user } = useAuth();
    const [state, setState] = useState({ count: 0, owner: null });

    useEffect(() => {
        const refresh = () => setState(readDraftState());
        refresh();
        const onStorage = (e) => { if (!e.key || e.key === DRAFT_KEY) refresh(); };
        window.addEventListener("storage", onStorage);
        window.addEventListener("tresses-draft-changed", refresh);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("tresses-draft-changed", refresh);
        };
    }, [location.pathname]); // re-check when navigating

    const { count, owner } = state;

    // Suppress on /book itself (would be redundant) and on admin
    if (count <= 0) return null;
    if (location.pathname.startsWith("/book") || location.pathname.startsWith("/admin")) return null;

    // Privacy guard: if the draft is owned by an account, only show it to that account.
    // - Logged-out viewer + owned draft → hide
    // - Logged-in viewer with different email → hide (the next login attempt will also wipe it)
    if (owner) {
        const me = user?.email ? user.email.toLowerCase() : null;
        if (!me || me !== owner.toLowerCase()) return null;
    }

    return (
        <Link
            to="/book"
            data-testid="resume-booking-pill"
            className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 border border-[#C29A5B]/50 bg-[#6B1535]/10 text-[#8F622C] text-[0.65rem] tracking-[0.22em] uppercase hover:bg-[#6B1535]/20 hover:border-[#C29A5B] transition-colors whitespace-nowrap"
            aria-label={`Resume booking with ${count} service${count === 1 ? "" : "s"} selected`}
            title={`${count} service${count === 1 ? "" : "s"} ready to book`}
        >
            <Bookmark size={11} className="fill-current" />
            <span>Resume</span>
            <span className="bg-[#6B1535] text-[#FFF8F2] px-1.5 py-0.5 text-[0.6rem] leading-none">{count}</span>
        </Link>
    );
}
